import type { CSSProperties } from "react";

export const expedienteColors = {
  rail: "#cbd5e1",
  railActive: "#6366f1",
  cardBg: "#ffffff",
  cardBorder: "#e2e8f0",
  cardShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
  title: "#0f172a",
  meta: "#64748b",
  iconBg: "#f1f5f9",
  iconColor: "#475569",
  obligatoryBg: "#fef2f2",
  obligatoryText: "#b91c1c",
  obligatoryBorder: "#fecaca",
  optionalBg: "#f8fafc",
  optionalText: "#475569",
  optionalBorder: "#e2e8f0",
  bodyBg: "#f8fafc",
  chevronBg: "#f1f5f9",
  accentObligatory: "#ef4444",
  accentOptional: "#94a3b8",
  statusBannerBg: "#f8fafc",
  statusBannerBorder: "#e2e8f0",
};

export const expedienteListStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  width: "100%",
  maxWidth: "100%",
  margin: 0,
  padding: "4px 0 12px",
  boxSizing: "border-box",
};

export const getExpedienteRowOuterStyle = (): CSSProperties => ({
  display: "flex",
  alignItems: "stretch",
  gap: 14,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
});

export const getExpedienteRailColumnStyle = (
  isLast: boolean,
  accentColor: string
): CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flexShrink: 0,
  width: 20,
  paddingTop: 4,
});

export const getExpedienteRailDotStyle = (
  accentColor: string,
  hasFiles: boolean
): CSSProperties => ({
  width: 12,
  height: 12,
  borderRadius: "50%",
  flexShrink: 0,
  backgroundColor: hasFiles ? accentColor : "#ffffff",
  border: `3px solid ${hasFiles ? accentColor : expedienteColors.rail}`,
  boxSizing: "border-box",
  zIndex: 1,
});

export const getExpedienteRailLineStyle = (isLast: boolean): CSSProperties => ({
  flex: 1,
  width: 2,
  marginTop: 6,
  marginBottom: isLast ? 0 : -12,
  backgroundColor: expedienteColors.rail,
  borderRadius: 1,
  minHeight: isLast ? 0 : 24,
});

export const getExpedienteCardStyle = (
  isNarrow: boolean,
  isExpanded: boolean
): CSSProperties => ({
  flex: 1,
  minWidth: 0,
  backgroundColor: expedienteColors.cardBg,
  border: `1px solid ${expedienteColors.cardBorder}`,
  borderRadius: 12,
  boxShadow: expedienteColors.cardShadow,
  overflow: "hidden",
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  ...(isExpanded
    ? {
        borderColor: "#c7d2fe",
        boxShadow: "0 4px 14px rgba(99, 102, 241, 0.12)",
      }
    : {}),
});

export const getExpedienteHeaderStyle = (
  isNarrow: boolean
): CSSProperties => ({
  display: "flex",
  flexDirection: isNarrow ? "column" : "row",
  alignItems: isNarrow ? "stretch" : "center",
  gap: isNarrow ? 10 : 12,
  padding: isNarrow ? "14px 14px 12px" : "14px 16px",
  cursor: "pointer",
  width: "100%",
  boxSizing: "border-box",
});

export const getExpedienteHeaderTopStyle = (
  isNarrow: boolean
): CSSProperties => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  flex: 1,
  minWidth: 0,
  width: isNarrow ? "100%" : "auto",
});

export const expedienteBadgeStyle = (obligatory: boolean): CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
  padding: "4px 10px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.02em",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  backgroundColor: obligatory
    ? expedienteColors.obligatoryBg
    : expedienteColors.optionalBg,
  color: obligatory
    ? expedienteColors.obligatoryText
    : expedienteColors.optionalText,
  border: `1px solid ${
    obligatory
      ? expedienteColors.obligatoryBorder
      : expedienteColors.optionalBorder
  }`,
});

export const expedienteIconBoxStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: 10,
  backgroundColor: expedienteColors.iconBg,
  color: expedienteColors.iconColor,
  flexShrink: 0,
  fontSize: 18,
};

export const expedienteTitleBlockStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

export const expedienteTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: expedienteColors.title,
  lineHeight: 1.35,
  wordBreak: "break-word",
};

export const expedienteMetaStyle: CSSProperties = {
  fontSize: 13,
  color: expedienteColors.meta,
  lineHeight: 1.4,
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 6,
};

export const getExpedienteActionsStyle = (
  isNarrow: boolean
): CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: isNarrow ? "space-between" : "flex-end",
  gap: 10,
  flexShrink: 0,
  width: isNarrow ? "100%" : "auto",
  marginLeft: isNarrow ? 0 : "auto",
});

export const expedienteChevronBtnStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  border: "none",
  borderRadius: 8,
  backgroundColor: expedienteColors.chevronBg,
  color: expedienteColors.iconColor,
  cursor: "pointer",
  flexShrink: 0,
};

export const expedienteBodyStyle: CSSProperties = {
  padding: "12px 16px 16px",
  borderTop: `1px solid ${expedienteColors.cardBorder}`,
  backgroundColor: expedienteColors.bodyBg,
};

export const expedienteEditBtnStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 500,
  color: "#334155",
  backgroundColor: "#ffffff",
  border: `1px solid ${expedienteColors.cardBorder}`,
  borderRadius: 8,
  cursor: "pointer",
};

export const expedienteStatusBannerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 14,
  padding: "10px 14px",
  borderRadius: 10,
  backgroundColor: expedienteColors.statusBannerBg,
  border: `1px solid ${expedienteColors.statusBannerBorder}`,
  fontSize: 13,
  color: expedienteColors.meta,
  fontWeight: 500,
  width: "100%",
  boxSizing: "border-box",
};

export const expedienteFadeInStyle = (delaySeconds: number): CSSProperties => ({
  animation: `expedienteFadeIn 0.45s ease-out ${delaySeconds}s forwards`,
  opacity: 0,
});

export const EXPEDIENTE_TIMELINE_KEYFRAMES = `
  @keyframes expedienteFadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;
